import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldAlert, Loader2 } from "lucide-react";

// Mandatory screen shown when a user's account carries must_change_password (owner-admin recovery).
export default function ForcePasswordChange() {
  const { user, setUser, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters.");
    if (next !== confirm) return setError("New password and confirmation do not match.");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const me = await api.get("/auth/me");
      setUser(me);
      toast.success("Password updated — welcome in.");
    } catch (err) {
      setError(formatApiErrorDetail(err.detail) || "Could not change password.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-100 px-4" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 shadow-lg p-7">
        <div className="flex items-center gap-2.5 mb-1">
          <span className="h-9 w-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center"><ShieldAlert className="h-5 w-5" /></span>
          <h1 className="font-display text-lg font-semibold text-slate-900">Set a new password</h1>
        </div>
        <p className="text-xs text-slate-500 mb-5">For security, <span className="font-medium text-slate-700">{user?.name || user?.username}</span> must choose a new password before continuing.</p>
        <form onSubmit={submit} className="space-y-3.5">
          <div className="space-y-1.5">
            <Label className="text-xs">Current password</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" required data-testid="fpc-current" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" placeholder="At least 8 characters" required data-testid="fpc-new" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" required data-testid="fpc-confirm" />
          </div>
          {error && <p className="text-xs text-rose-600" data-testid="fpc-error">{error}</p>}
          <Button type="submit" disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="fpc-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password & continue"}
          </Button>
          <button type="button" onClick={logout} className="w-full text-xs text-slate-400 hover:text-slate-600" data-testid="fpc-logout">Sign out instead</button>
        </form>
      </div>
    </div>
  );
}
